import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BarChart3, CheckSquare2, House, LayoutDashboard, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import authService from "../services/authService";
import { useQuiz } from "../context/QuizContext";
import { Button, IconButton } from "./ui/button";
import { UserAvatar } from "./ui/data-display";
import { cn } from "../lib/utils";

const getDisplayName = (name, email) => {
  const savedName = String(name || "").trim();
  if (savedName && savedName.toLowerCase() !== "user") return savedName;

  const emailName = String(email || "").split("@")[0];
  const derivedName = emailName
    .split(/[._-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

  return derivedName || "User";
};

const TopHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, setUserInfo } = useQuiz();
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const info = authService.getUserInfo();
    if (info?.email && (!userInfo?.email || !userInfo?.name)) setUserInfo(info);
    authService.getCurrentUser().then(current => {
      if (cancelled) return;
      setUserInfo({ email: current.email, name: current.name });
      setIsAdmin(Boolean(current.is_admin));
    }).catch(() => { if (!cancelled) setIsAdmin(false); });
    return () => { cancelled = true; };
  }, [setUserInfo, userInfo?.email, userInfo?.name]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const logout = () => { authService.logout(); navigate("/"); };
  const quizActive = location.pathname === "/quiz" || location.pathname === "/microsoft-test";
  const adminActive = location.pathname.startsWith("/admin");
  const displayName = getDisplayName(userInfo?.name, userInfo?.email);
  const links = [
    { label: "Learning", path: "/education", icon: House, show: !quizActive },
    { label: "My Results", path: "/result", icon: BarChart3, show: !quizActive },
    { label: "Checklist", path: "/checklist", icon: CheckSquare2, show: !quizActive },
    { label: "Admin", path: "/admin", icon: LayoutDashboard, show: !quizActive && isAdmin },
  ].filter(item => item.show);

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-16 border-b border-[#2a4058] bg-[#192b40] shadow-[0_3px_12px_rgba(15,23,42,0.16)]">
      <div className="app-container flex h-full items-center gap-4">
        <button onClick={() => navigate(quizActive ? location.pathname : "/education")} className="flex min-w-0 items-center gap-3 rounded-md text-left" aria-label="PhishGuard home">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-white"><ShieldCheck className="h-5 w-5" /></span>
          <span className="hidden leading-tight xs:block sm:block"><span className="block text-sm font-bold text-white">PhishGuard</span><span className="block text-[11px] text-slate-300">I2E Security Academy</span></span>
        </button>

        {quizActive ? (
          <div className="ml-auto flex items-center gap-2 rounded-full bg-sky-400/15 px-3 py-1.5 text-xs font-semibold text-sky-100"><span className="h-2 w-2 rounded-full bg-sky-400" />Assessment in progress</div>
        ) : (
          <>
            <nav className="ml-auto hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
              {links.map(({ label, path, icon: Icon }) => {
                const active = path === "/admin" ? adminActive : location.pathname.startsWith(path);
                return <Button key={path} variant="ghost" size="sm" onClick={() => navigate(path)} className={cn("!text-slate-200 hover:!bg-white/10 hover:!text-white", active && "!bg-primary-subtle !text-primary-hover ring-1 ring-primary/20 hover:!bg-primary-subtle hover:!text-primary-hover")} aria-current={active ? "page" : undefined}><Icon className="h-4 w-4" />{label}</Button>;
              })}
            </nav>
            <div className="hidden w-64 shrink-0 items-center gap-3 border-l border-white/20 pl-4 lg:flex">
              <UserAvatar name={displayName} email={userInfo?.email} />
              <div className="min-w-0 flex-1 leading-tight"><p className="truncate text-sm font-semibold text-white">{displayName}</p><p className="truncate text-xs text-slate-300">{userInfo?.email}</p></div>
              <IconButton label="Log out" variant="ghost" className="!text-slate-200 hover:!bg-white/10 hover:!text-white" onClick={logout}><LogOut className="h-4 w-4" /></IconButton>
            </div>
            <IconButton label={mobileOpen ? "Close menu" : "Open menu"} variant="ghost" className="ml-auto !text-slate-200 hover:!bg-white/10 hover:!text-white lg:hidden" onClick={() => setMobileOpen(v => !v)}>{mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</IconButton>
          </>
        )}
      </div>

      {mobileOpen && !quizActive && (
        <div className="absolute inset-x-0 top-16 border-b border-[#2a4058] bg-[#192b40] py-4 shadow-card lg:hidden">
          <div className="app-container space-y-1">
            <div className="mb-3 flex items-center gap-3 border-b border-white/20 pb-3"><UserAvatar name={displayName} email={userInfo?.email} /><div className="min-w-0"><p className="truncate text-sm font-semibold text-white">{displayName}</p><p className="truncate text-xs text-slate-300">{userInfo?.email}</p></div></div>
            {links.map(({ label, path, icon: Icon }) => <Button key={path} variant="ghost" className="w-full justify-start !text-slate-200 hover:!bg-white/10 hover:!text-white" onClick={() => navigate(path)}><Icon className="h-4 w-4" />{label}</Button>)}
            <Button variant="ghost" className="w-full justify-start !text-red-300 hover:!bg-red-500/15 hover:!text-red-200" onClick={logout}><LogOut className="h-4 w-4" />Log out</Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopHeader;
